
import { useShallow } from 'zustand/react/shallow';

import { DataModelAttribute } from '@/types/DataModel';

import { useAttributesStateStore } from './useAttributesStore';

interface IsHiddenAttributeParams {
    attribute?: DataModelAttribute;
    isHiddenByParent?: boolean;
}

// Non-hook helper for callbacks and utilities; reads hidden state by attribute name from the store.
const isHiddenAttribute = ({
    attribute,
    isHiddenByParent = false,
}: IsHiddenAttributeParams): boolean => {
    if (!attribute) return false;

    const state = useAttributesStateStore.getState();
    const isHiddenByState = state.hidden[attribute.name] ?? false;
    const isHiddenByAdditionalParams = attribute.additionalParams?.hidden || false;

    // Always check all possible sources of hidden state
    // This ensures we don't miss any hidden attributes regardless of initialization state
    return isHiddenByParent || isHiddenByState || isHiddenByAdditionalParams;
};

// Hook wrapper for React components; reads hidden state by fieldPath so nested form fields are handled correctly.
const useIsHiddenAttribute = (attribute: DataModelAttribute, fieldPath: string, isHiddenByParent: boolean) => {

    const { hidden } = useAttributesStateStore(useShallow(state => ({
        hidden: state.hidden[fieldPath] ?? false
    })));

    const isHiddenByAdditionalParams = attribute.additionalParams?.hidden || false;

    // Always check all possible sources of hidden state
    // This ensures we don't miss any hidden attributes regardless of initialization state
    return isHiddenByParent || hidden || isHiddenByAdditionalParams;

};

const useAreAllAttributesHidden = (fieldPaths: string[]) => {
    const isHiddenByAttributeState = useAttributesStateStore(
        useShallow((state) => fieldPaths.map(path => state.hidden[path] ?? false))  // Handle undefined by defaulting to false
    );

    return isHiddenByAttributeState.every((isHidden)=> isHidden === true);
};

export { isHiddenAttribute, useAreAllAttributesHidden, useIsHiddenAttribute };
